"use client";

import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Loader2 } from "lucide-react";

import { useWidgetTemplates, WidgetTemplate } from "@/modules/widget-templates";
import { Widget } from "@/modules/widgets/types";
import { shareEndpoints } from "@/modules/widgets/api/shareEndpoints";
import { CategorySidebar } from "./CategorySidebar";
import { TemplateCard } from "./TemplateCard";
import { SharedWidgetCard } from "./SharedWidgetCard";

interface TemplatePickerProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectTemplate: (template: WidgetTemplate) => void;
    onSelectSharedWidget?: (widget: Widget) => void;
}

export function TemplatePicker({ isOpen, onClose, onSelectTemplate, onSelectSharedWidget }: TemplatePickerProps) {
    const [activeTab, setActiveTab] = useState<"templates" | "shared">("templates");
    const [sharedWidgets, setSharedWidgets] = useState<Widget[]>([]);
    const [sharedLoading, setSharedLoading] = useState(false);
    const [selectedSharedWidget, setSelectedSharedWidget] = useState<Widget | null>(null);

    const {
        categories,
        filteredTemplates,
        selectedCategory,
        searchQuery,
        selectedTemplate,
        isLoading,
        error,
        setSelectedCategory,
        setSearchQuery,
        setSelectedTemplate,
        fetchTemplates,
        reset,
    } = useWidgetTemplates();

    useEffect(() => {
        if (isOpen) {
            fetchTemplates();
        } else {
            reset();
            setActiveTab("templates");
            setSharedWidgets([]);
            setSelectedSharedWidget(null);
        }
    }, [isOpen, fetchTemplates, reset]);

    const handleSwitchToShared = async () => {
        setActiveTab("shared");
        setSelectedTemplate(null);
        if (sharedWidgets.length === 0) {
            setSharedLoading(true);
            try {
                const widgets = await shareEndpoints.getMySharedWidgets();
                setSharedWidgets(widgets);
            } finally {
                setSharedLoading(false);
            }
        }
    };

    const handleRemoveSharedWidget = async (widgetId: number) => {
        await shareEndpoints.removeMySharedWidget(widgetId);
        setSharedWidgets((prev) => prev.filter((w) => w.id !== widgetId));
        if (selectedSharedWidget?.id === widgetId) setSelectedSharedWidget(null);
    };

    const handleConfirm = () => {
        if (activeTab === "shared" && selectedSharedWidget) {
            onSelectSharedWidget?.(selectedSharedWidget);
        } else if (selectedTemplate) {
            onSelectTemplate(selectedTemplate);
        }
    };

    const isConfirmEnabled = activeTab === "shared" ? !!selectedSharedWidget : !!selectedTemplate;
    const confirmLabel = activeTab === "shared" ? "Clone Widget" : "Select Template";

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) handleClose();
            }}
        >
            <AnimatePresence>
                {isOpen && (
                    <Dialog.Portal forceMount>
                        {/* Backdrop */}
                        <Dialog.Overlay asChild>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="fixed inset-0 z-[10000] bg-[#0f1b21]/30"
                            />
                        </Dialog.Overlay>

                        {/* Modal */}
                        <Dialog.Content asChild>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.97 }}
                                transition={{ duration: 0.2 }}
                                className="fixed inset-0 z-[10001] flex items-center justify-center p-6"
                            >
                                <div className="flex h-[85vh] w-full max-w-[960px] flex-col rounded-2xl bg-white shadow-2xl">
                                    {/* Header */}
                                    <div className="flex items-center justify-between px-8 pt-7 pb-5">
                                        <Dialog.Title className="text-[28px] font-bold text-[#2d3a3f]">Start from Template</Dialog.Title>
                                        <Dialog.Close asChild>
                                            <button type="button" className="text-[#5a717a] transition hover:text-[#2d3a3f]" aria-label="Close template picker">
                                                <X className="h-5 w-5" />
                                            </button>
                                        </Dialog.Close>
                                    </div>

                                    {/* Search */}
                                    <div className="px-8 pb-4">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5a717a]" />
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                placeholder="Search by widget name or category..."
                                                className="w-full rounded-lg border border-[#e0e0e0] py-2.5 pl-10 pr-4 text-sm text-[#2d3a3f] placeholder-[#a0b5bc] outline-none transition focus:border-[#079a95] focus:ring-1 focus:ring-[#079a95] font-roboto"
                                            />
                                            {searchQuery && (
                                                <button
                                                    type="button"
                                                    onClick={() => setSearchQuery("")}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a717a] hover:text-[#2d3a3f]"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Body */}
                                    <div className="flex flex-1 min-h-0 overflow-hidden">
                                        {/* Sidebar */}
                                        <div className="overflow-y-auto py-2 pl-8">
                                            <div className="w-44 shrink-0 pr-4">
                                                {/* Shared with me tab */}
                                                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-[#5a717a] font-roboto">Library</p>
                                                <button
                                                    type="button"
                                                    onClick={handleSwitchToShared}
                                                    className={`w-full rounded-r-md px-3 py-2 text-left text-sm font-roboto transition-colors mb-4 ${
                                                        activeTab === "shared"
                                                            ? "border-l-[3px] border-[#079a95] bg-[#e8f5f4] font-semibold text-[#079a95]"
                                                            : "border-l-[3px] border-transparent text-[#2d3a3f] hover:bg-[#f3f5f6]"
                                                    }`}
                                                >
                                                    Shared with me
                                                </button>

                                                {/* Template categories */}
                                                {categories.length > 0 && (
                                                    <CategorySidebar
                                                        categories={categories}
                                                        selectedCategory={activeTab === "templates" ? selectedCategory : null}
                                                        onSelectCategory={(slug) => {
                                                            setActiveTab("templates");
                                                            setSelectedSharedWidget(null);
                                                            setSelectedCategory(slug);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Content grid */}
                                        <div className="flex-1 overflow-y-auto px-6 py-2">
                                            {activeTab === "shared" ? (
                                                sharedLoading ? (
                                                    <div className="flex h-full items-center justify-center">
                                                        <Loader2 className="h-8 w-8 animate-spin text-[#079a95]" />
                                                    </div>
                                                ) : sharedWidgets.length === 0 ? (
                                                    <div className="flex h-full items-center justify-center">
                                                        <p className="text-sm text-[#5a717a] font-roboto">No widgets have been shared with you yet.</p>
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-2 gap-4">
                                                        {sharedWidgets.map((widget) => (
                                                            <SharedWidgetCard
                                                                key={widget.id}
                                                                widget={widget}
                                                                isSelected={selectedSharedWidget?.id === widget.id}
                                                                onClick={() => setSelectedSharedWidget(selectedSharedWidget?.id === widget.id ? null : widget)}
                                                                onRemove={() => handleRemoveSharedWidget(widget.id)}
                                                            />
                                                        ))}
                                                    </div>
                                                )
                                            ) : isLoading ? (
                                                <div className="flex h-full items-center justify-center">
                                                    <Loader2 className="h-8 w-8 animate-spin text-[#079a95]" />
                                                </div>
                                            ) : error ? (
                                                <div className="flex h-full items-center justify-center">
                                                    <div className="text-center">
                                                        <p className="text-sm text-red-600 font-roboto">{error}</p>
                                                        <button type="button" onClick={fetchTemplates} className="mt-2 text-sm font-medium text-[#079a95] hover:text-[#067a76] font-roboto">
                                                            Try again
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : filteredTemplates.length === 0 ? (
                                                <div className="flex h-full items-center justify-center">
                                                    <p className="text-sm text-[#5a717a] font-roboto">
                                                        {searchQuery ? "No templates match your search." : selectedCategory ? "No templates available in this category yet." : "No templates available."}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-2 gap-4">
                                                    {filteredTemplates.map((template) => (
                                                        <TemplateCard
                                                            key={template.id}
                                                            template={template}
                                                            isSelected={selectedTemplate?.id === template.id}
                                                            onClick={() => setSelectedTemplate(selectedTemplate?.id === template.id ? null : template)}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="border-t border-[#e0e0e0] px-8 py-5">
                                        <div className="flex items-center justify-between">
                                            <button
                                                type="button"
                                                onClick={handleClose}
                                                className="text-sm font-medium text-[#2d3a3f] transition hover:text-[#5a717a] font-roboto"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleConfirm}
                                                disabled={!isConfirmEnabled}
                                                className={`rounded-lg px-6 py-2.5 text-sm font-medium text-white transition font-roboto ${
                                                    !isConfirmEnabled ? "cursor-not-allowed bg-[#c5d0d4]" : "bg-[#079a95] hover:bg-[#067a76]"
                                                }`}
                                            >
                                                {confirmLabel}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </Dialog.Content>
                    </Dialog.Portal>
                )}
            </AnimatePresence>
        </Dialog.Root>
    );
}
